import { useMemo } from 'react';
import {
	EnvironmentTypesEnum,
	TFormikCommonProps,
	TOptions,
	adoOrganizationNamespaces,
	adoProjectNamespaces,
	repositoryBranchNamespaces,
	repositoryIdNamespaces,
	repositoryNameSpaces,
	sourceControlIntegrationIdNameSpaces,
} from '../../pages/create-project/helpers';
import {
	InputFormControlWithTransformationMessage,
	SelectFormControl,
} from '../../shared-components';
import { Stack } from '@chakra-ui/react';
import { FormikValues } from '../../pages/create-project/hooks/CreateProjectContext';
import {
	editCreateEnv,
	fifthStep,
	secondStep,
} from '../../../@xmcloud/core/messages/en';
import {
	useGetADOOrganizations,
	useGetADOProfile,
	useGetADOProjects,
	useGetADORepositories,
} from '../../services/adoApi';
import { SelectADOBranch } from '.';

const { repoNameHelperText, nameYourRepo } = secondStep;
const { repo: repotxt } = fifthStep;
const { adoOrg, adoProject } = editCreateEnv;

export const AdoRepository: React.FC<
	{
		isIntegrationIdValidationValid: boolean;
		isSourceControlIntegrationLoading: boolean;
		accessToken: string;
		nameSpace?: EnvironmentTypesEnum | 'default';
		setValues: (values: any) => void | any;
		setFieldValue: (field: string, value: any) => void;
		setFieldTouched: any;
		isByoc?: boolean;
		handleChange: any;
		initialRepository?: string;
		initialRepositoryBranch?: string;
		initialAdoOrganization?: string;
		initialAdoProjectName?: string;
	} & TFormikCommonProps
> = ({
	isIntegrationIdValidationValid,
	accessToken,
	isSourceControlIntegrationLoading,
	nameSpace = 'default',
	values,
	errors,
	touched,
	setValues,
	setFieldValue,
	setFieldTouched,
	handleChange,
	isByoc = false,
	initialRepository = '',
	initialRepositoryBranch = '',
	initialAdoOrganization = '',
	initialAdoProjectName = '',
}) => {
	const repositoryNamespace = repositoryNameSpaces[nameSpace];
	const repositoryIdNamespace = repositoryIdNamespaces[nameSpace];
	const repositoryBranchNamespace = repositoryBranchNamespaces[nameSpace];
	const sourceControlIntegrationIdNameSpace =
		sourceControlIntegrationIdNameSpaces[nameSpace];

	const adoOrganizationNamespace = adoOrganizationNamespaces[nameSpace];
	const adoProjectNamespace = adoProjectNamespaces[nameSpace];

	const sourceControlIntegrationId =
		values[sourceControlIntegrationIdNameSpace] || '';

	const { data: profileData, isLoading: isLoadingProfile } = useGetADOProfile(
		accessToken,
		sourceControlIntegrationId,
	);
	const publicAlias = profileData?.data.publicAlias || '';

	const { data: organizationsData, isLoading: isAdoOrganizationLoading } =
		useGetADOOrganizations({
			token: accessToken,
			publicAlias,
			_enabled: isIntegrationIdValidationValid && !!accessToken,
		});

	const { data: projectsData, isLoading: isLoadingProjects } =
		useGetADOProjects({
			token: accessToken,
			organization: values[adoOrganizationNamespace] || '',
			_enabled: isIntegrationIdValidationValid && !!accessToken,
		});

	const { data: repositoriesData, isFetching: isFetchingRepositories } =
		useGetADORepositories({
			token: accessToken,
			organization: values[adoOrganizationNamespace] || '',
			project: values[adoProjectNamespace] || '',
			_enabled: isIntegrationIdValidationValid && !!accessToken,
		});

	const adoOrganizations = useMemo(
		() =>
			organizationsData?.data.value.map((r: any) => ({
				label: r.accountName,
				value: r.accountName,
			})) || [],
		[organizationsData?.data.value],
	);

	const adoProjects = useMemo(
		() =>
			projectsData?.data.value.map((r: any) => ({
				label: r.name,
				value: r.name,
			})) || [],
		[projectsData?.data.value],
	);
	const repositoryOptions: TOptions = useMemo(
		() =>
			repositoriesData?.data.value.map((r: any) => ({
				label: r.name,
				value: r.name,
				id: r.id,
			})) || [],
		[repositoriesData?.data.value],
	);

	const repositoryId = values?.[repositoryIdNamespace] ?? '';

	const displayAdoOrganizations = useMemo(() => {
		if (
			!initialAdoOrganization ||
			adoOrganizations.some(
				(o: any) => o.value === initialAdoOrganization,
			)
		) {
			return adoOrganizations;
		}
		return [
			{ label: initialAdoOrganization, value: initialAdoOrganization },
			...adoOrganizations,
		];
	}, [adoOrganizations, initialAdoOrganization]);

	const displayAdoProjects = useMemo(() => {
		if (
			!initialAdoProjectName ||
			adoProjects.some((o: any) => o.value === initialAdoProjectName)
		) {
			return adoProjects;
		}
		return [
			{ label: initialAdoProjectName, value: initialAdoProjectName },
			...adoProjects,
		];
	}, [adoProjects, initialAdoProjectName]);

	const displayRepositoryOptions = useMemo(() => {
		if (
			!initialRepository ||
			repositoryOptions.some((o: any) => o.value === initialRepository)
		) {
			return repositoryOptions;
		}
		return [
			{
				label: initialRepository,
				value: initialRepository,
				id: repositoryId,
			},
			...repositoryOptions,
		];
	}, [repositoryOptions, initialRepository, repositoryId]);

	const onOrganizationChange = (e: any) => {
		setValues((prev: FormikValues) => ({
			...prev,
			[adoOrganizationNamespace]: e.value || '',
			[adoProjectNamespace]: '',
			[repositoryNamespace]: '',
			[repositoryIdNamespace]: '',
			[repositoryBranchNamespace]: '',
		}));
	};
	const onProjectChange = (e: any) => {
		setValues((prev: FormikValues) => ({
			...prev,
			[adoProjectNamespace]: e.value || '',
			[repositoryNamespace]: '',
			[repositoryIdNamespace]: '',
			[repositoryBranchNamespace]: '',
		}));
	};
	const onRepositoryChange = (e: any) => {
		setValues((prev: FormikValues) => ({
			...prev,
			[repositoryNamespace]: e.label || '',
			[repositoryIdNamespace]: `${e.id}` || '',
			[repositoryBranchNamespace]: '',
		}));
	};

	return (
		<Stack gap="7">
			<SelectFormControl
				{...{
					isInvalid: Boolean(
						errors[adoOrganizationNamespace] &&
							touched[adoOrganizationNamespace],
					),
					isLoading:
						isAdoOrganizationLoading ||
						isLoadingProfile ||
						isSourceControlIntegrationLoading,
					options: displayAdoOrganizations,
					onChange: onOrganizationChange,
					label: adoOrg,
					name: adoOrganizationNamespace,
					currentValue: values[adoOrganizationNamespace],
					onFocus: () =>
						setFieldTouched(adoOrganizationNamespace, true),
					error: errors[adoOrganizationNamespace],
					isRequired: true,
					pt: 0,
				}}
			/>
			<SelectFormControl
				{...{
					isInvalid: Boolean(
						errors[adoProjectNamespace] &&
							touched[adoProjectNamespace],
					),
					isLoading:
						isLoadingProjects || isSourceControlIntegrationLoading,
					options: displayAdoProjects,
					onChange: onProjectChange,
					label: adoProject,
					name: adoProjectNamespace,
					currentValue: values[adoProjectNamespace],
					onFocus: () => setFieldTouched(adoProjectNamespace, true),
					error: errors[adoProjectNamespace],
					isRequired: true,
					pt: 0,
				}}
			/>
			{isByoc && (
				<SelectFormControl
					{...{
						error: errors[repositoryNamespace],
						isInvalid: Boolean(
							errors[repositoryNamespace] &&
								touched[repositoryNamespace],
						),
						isLoading:
							isFetchingRepositories ||
							isSourceControlIntegrationLoading,
						options: displayRepositoryOptions,
						onChange: onRepositoryChange,
						label: repotxt,
						name: repositoryNamespace,
						currentValue: values[repositoryNamespace],
						onFocus: () =>
							setFieldTouched(repositoryNamespace, true),
						isRequired: true,
						pt: 0,
					}}
				/>
			)}
			{isByoc && (
				<SelectADOBranch
					{...{
						token: accessToken,
						nameSpace,
						values,
						errors,
						touched,
						setFieldValue,
						setFieldTouched,
						fallbackBranch: initialRepositoryBranch,
					}}
				/>
			)}
			{!isByoc && (
				<InputFormControlWithTransformationMessage
					{...{
						isInvalid: Boolean(
							errors[repositoryNamespace] &&
								touched[repositoryNamespace],
						),
						label: nameYourRepo,
						name: repositoryNamespace,
						value: values[repositoryNamespace],
						pt: 0,
						onChange: handleChange,
						error: errors[repositoryNamespace],
						onFocus: () =>
							setFieldTouched(repositoryNamespace, true),
						setFieldValue,
						isRequired: true,
						helperText: repoNameHelperText,
					}}
				/>
			)}
		</Stack>
	);
};
